<script lang="ts">
    import './controls.css';

    import { cn } from '$lib/utils/cn';

    import RangeSlider from 'svelte-range-slider-pips';

    import type { Language } from '$lib/types/Language';
    import type { SeekTo } from './Video.types';

    export let volume = 0;
    export let changeVolume: (volume: number) => void;

    export let duration = 0;
    export let playedDuration = 0;
    export let seekTo: SeekTo = () => {};

    export let playing = false;
    export let hasVisibleCaptions = false;
    export let isCCButtonVisible = false;
    export let togglePlaying;
    export let language: Language = 'en';

    export let muted = false;
    export let toggleMuted;
    export let toggleCaptions;

    export let toggleFullscreen;

    let isVolumeSliderVisible = false;

    const TRANSLATIONS = {
        en: {
            play: 'Play video',
            pause: 'Pause video',
            mute: 'Mute',
            unmute: 'Unmute',
        },
        de: {
            play: 'Video abspielen',
            pause: 'Video pausieren',
            mute: 'Stummschalten',
            unmute: 'Ton aktivieren',
        },
    };

    const rangeSpringValues = {
        stiffness: 0.4,
        damping: 0.8,
    };

    const convertSecondsToTime = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.ceil(seconds % 60);

        const formattedHours = hours > 0 ? String(hours).padStart(2, '0') : '';
        const formattedMinutes = String(minutes).padStart(2, '0');
        const formattedSeconds = String(remainingSeconds).padStart(2, '0');

        return hours > 0
            ? `${formattedHours}:${formattedMinutes}:${formattedSeconds}`
            : `${formattedMinutes}:${formattedSeconds}`;
    };

    const adjustVolume = (event) => {
        changeVolume(event.detail.value);
    };

    const onDurationChange = (event) => {
        seekTo(event.detail.value, 'seconds');
    };

    const showVolumeSlider = () => {
        isVolumeSliderVisible = true;
    };

    const hideVolumeSlider = () => {
        isVolumeSliderVisible = false;
    };

    const playingText = playing ? TRANSLATIONS[language].pause : TRANSLATIONS[language].play;
</script>

<div
    class="fun-absolute fun-bottom-4 fun-z-10 fun-mx-4 fun-flex fun-h-9 fun-w-[calc(100%-2rem)] fun-items-center fun-gap-2 fun-rounded-xl fun-bg-neutral-900/60 fun-px-2 fun-opacity-0 fun-backdrop-blur-sm fun-transition-opacity fun-duration-300 group-hover/video:fun-opacity-100"
>
    <!-- Play Button -->
    <button on:click={togglePlaying} aria-label={playingText}>
        {#if playing}
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
            >
                <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M6.75 4C7.16421 4 7.5 4.33579 7.5 4.75V15.25C7.5 15.6642 7.16421 16 6.75 16C6.33579 16 6 15.6642 6 15.25V4.75C6 4.33579 6.33579 4 6.75 4ZM13.25 4C13.6642 4 14 4.33579 14 4.75L14 15.25C14 15.6642 13.6642 16 13.25 16C12.8358 16 12.5 15.6642 12.5 15.25L12.5 4.75C12.5 4.33579 12.8358 4 13.25 4Z"
                    fill="white"
                />
            </svg>
        {:else}
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
            >
                <path
                    d="M6.29995 2.84095C5.3011 2.21124 4 2.92906 4 4.10984V15.891C4 17.0718 5.3011 17.7896 6.29995 17.1599L15.6436 11.2693C16.577 10.6809 16.577 9.31997 15.6436 8.73152L6.29995 2.84095Z"
                    fill="white"
                />
            </svg>
        {/if}
    </button>

    <!-- Duration Slider -->
    <RangeSlider
        id="duration-slider-container"
        min={0}
        max={duration}
        values={[playedDuration]}
        range="min"
        on:change={onDurationChange}
        hoverable={false}
        springValues={rangeSpringValues}
        step={0.1}
        ariaLabels={[language === 'de' ? 'Videolänge' : 'Video duration']}
    />

    <div
        class="fun-flex fun-w-24 fun-items-center fun-space-x-1 fun-rounded-md fun-text-xs fun-text-white"
    >
        <span class="fun-w-9 fun-min-w-8 fun-max-w-9 fun-font-bold fun-text-white"
            >{convertSecondsToTime(playedDuration)}</span
        >
        <span class="fun-opacity-50">/</span>
        <span class="fun-w-9 fun-min-w-8 fun-max-w-9 fun-opacity-50"
            >{convertSecondsToTime(duration)}</span
        >
    </div>

    <!-- Volume Button -->
    <div
        class="fun-flex fun-h-full fun-items-center"
        on:mouseenter={showVolumeSlider}
        on:mouseleave={hideVolumeSlider}
    >
        <button
            on:click={(e) => {
                e.stopPropagation();
                toggleMuted();
            }}
            class="fun-relative"
            aria-label={muted ? TRANSLATIONS[language].unmute : TRANSLATIONS[language].mute}
        >
            {#if muted}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                >
                    <path
                        d="M9.54747 3.06153C9.82215 3.18021 10 3.4508 10 3.75002V16.25C10 16.5492 9.82215 16.8198 9.54747 16.9385C9.2728 17.0572 8.95387 17.0012 8.73598 16.7962L4.70257 13H3.16724C2.85725 13 2.5792 12.8093 2.46756 12.5201C2.16534 11.7372 2 10.887 2 10C2 9.11302 2.16534 8.26287 2.46756 7.47993C2.5792 7.19074 2.85725 7.00002 3.16724 7.00002H4.70257L8.73598 3.20387C8.95387 2.99879 9.2728 2.94286 9.54747 3.06153Z"
                        fill="white"
                    />
                    <path
                        d="M13.2803 7.2197C12.9874 6.92681 12.5126 6.92681 12.2197 7.2197C11.9268 7.51259 11.9268 7.98747 12.2197 8.28036L13.9393 10L12.2197 11.7197C11.9268 12.0126 11.9268 12.4875 12.2197 12.7804C12.5126 13.0733 12.9874 13.0733 13.2803 12.7804L15 11.0607L16.7197 12.7804C17.0126 13.0733 17.4874 13.0733 17.7803 12.7804C18.0732 12.4875 18.0732 12.0126 17.7803 11.7197L16.0607 10L17.7803 8.28036C18.0732 7.98747 18.0732 7.51259 17.7803 7.2197C17.4874 6.92681 17.0126 6.92681 16.7197 7.2197L15 8.93937L13.2803 7.2197Z"
                        fill="white"
                    />
                </svg>
            {:else}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                >
                    <path
                        d="M10 3.75002C10 3.4508 9.82215 3.18021 9.54747 3.06153C9.2728 2.94286 8.95387 2.99879 8.73598 3.20387L4.70257 7.00002H3.16724C2.85725 7.00002 2.5792 7.19074 2.46756 7.47993C2.16534 8.26287 2 9.11302 2 10C2 10.887 2.16534 11.7372 2.46756 12.5201C2.5792 12.8093 2.85725 13 3.16724 13H4.70257L8.73598 16.7962C8.95387 17.0012 9.2728 17.0572 9.54747 16.9385C9.82215 16.8198 10 16.5492 10 16.25V3.75002Z"
                        fill="white"
                    />
                    <path
                        d="M15.9498 5.05029C15.6569 4.7574 15.182 4.7574 14.8891 5.05029C14.5962 5.34318 14.5962 5.81806 14.8891 6.11095C17.037 8.25883 17.037 11.7412 14.8891 13.8891C14.5962 14.182 14.5962 14.6569 14.8891 14.9498C15.182 15.2427 15.6569 15.2427 15.9498 14.9498C18.6834 12.2161 18.6834 7.78396 15.9498 5.05029Z"
                        fill="white"
                    />
                    <path
                        d="M13.8287 7.1716C13.5358 6.8787 13.0609 6.8787 12.768 7.1716C12.4751 7.46449 12.4751 7.93936 12.768 8.23226C13.7443 9.20857 13.7443 10.7915 12.768 11.7678C12.4751 12.0607 12.4751 12.5356 12.768 12.8285C13.0609 13.1213 13.5358 13.1213 13.8287 12.8285C15.3908 11.2664 15.3908 8.73369 13.8287 7.1716Z"
                        fill="white"
                    />
                </svg>
            {/if}
        </button>

        <div
            class={cn(
                'fun-absolute fun-bottom-9 fun-right-[28px] fun-opacity-0 fun-backdrop-blur-sm fun-transition-opacity fun-duration-300',
                {
                    'fun-opacity-100': isVolumeSliderVisible,
                    'fun-right-[60px]': isCCButtonVisible,
                },
            )}
        >
            <div
                class="fun-mb-2 fun-flex fun-content-center fun-rounded-xl fun-bg-neutral-900/60 fun-px-2 fun-backdrop-blur-sm"
            >
                <RangeSlider
                    id="volume-slider-container"
                    min={0}
                    max={100}
                    values={[volume]}
                    on:change={adjustVolume}
                    hoverable={false}
                    range="min"
                    vertical
                    springValues={rangeSpringValues}
                    step={0.1}
                    ariaLabels={[language === 'de' ? 'Lautstärke' : 'Volume']}
                />
            </div>
        </div>
    </div>

    <!-- CC Button -->
    {#if isCCButtonVisible}
        <button
            on:click={toggleCaptions}
            class={cn('fun-font-mono fun-text-white', { 'fun-opacity-50': !hasVisibleCaptions })}
            aria-label={language === 'de' ? 'Untertitel aktivieren' : 'Toggle subtitles'}
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
            >
                <path
                    d="M20 4H4c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h16c1.103 0 2-.897 2-2V6c0-1.103-.897-2-2-2m-9 6H8v4h3v2H8c-1.103 0-2-.897-2-2v-4c0-1.103.897-2 2-2h3zm7 0h-3v4h3v2h-3c-1.103 0-2-.897-2-2v-4c0-1.103.897-2 2-2h3z"
                />
            </svg>
        </button>
    {/if}

    <!-- Fullscreen Button -->
    <button
        on:click={toggleFullscreen}
        aria-label={language === 'de' ? 'Vollbild aktivieren' : 'Toggle fullscreen'}
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
        >
            <path
                d="M13.2803 7.78033L16.5 4.56066V7.25C16.5 7.66421 16.8358 8 17.25 8C17.6642 8 18 7.66421 18 7.25V2.75C18 2.33579 17.6642 2 17.25 2H12.75C12.3358 2 12 2.33579 12 2.75C12 3.16421 12.3358 3.5 12.75 3.5H15.4393L12.2197 6.71967C11.9268 7.01256 11.9268 7.48744 12.2197 7.78033C12.5126 8.07322 12.9874 8.07322 13.2803 7.78033Z"
                fill="white"
            />
            <path
                d="M2 17.25V12.75C2 12.3358 2.33579 12 2.75 12C3.16421 12 3.5 12.3358 3.5 12.75V15.4393L6.71967 12.2197C7.01256 11.9268 7.48744 11.9268 7.78033 12.2197C8.07322 12.5126 8.07322 12.9874 7.78033 13.2803L4.56066 16.5H7.25C7.66421 16.5 8 16.8358 8 17.25C8 17.6642 7.66421 18 7.25 18H2.75C2.55806 18 2.36612 17.9268 2.21967 17.7803C2.14776 17.7084 2.09351 17.6255 2.05691 17.5371C2.02024 17.4487 2 17.3517 2 17.25Z"
                fill="white"
            />
            <path
                d="M12.2197 13.2803L15.4393 16.5H12.75C12.3358 16.5 12 16.8358 12 17.25C12 17.6642 12.3358 18 12.75 18H17.25C17.4419 18 17.6339 17.9268 17.7803 17.7803C17.8522 17.7084 17.9065 17.6255 17.9431 17.5371C17.9798 17.4487 18 17.3517 18 17.25V12.75C18 12.3358 17.6642 12 17.25 12C16.8358 12 16.5 12.3358 16.5 12.75V15.4393L13.2803 12.2197C12.9874 11.9268 12.5126 11.9268 12.2197 12.2197C11.9268 12.5126 11.9268 12.9874 12.2197 13.2803Z"
                fill="white"
            />
            <path
                d="M3.5 4.56066L6.71967 7.78033C7.01256 8.07322 7.48744 8.07322 7.78033 7.78033C8.07322 7.48744 8.07322 7.01256 7.78033 6.71967L4.56066 3.5H7.25C7.66421 3.5 8 3.16421 8 2.75C8 2.33579 7.66421 2 7.25 2H2.75C2.33579 2 2 2.33579 2 2.75V7.25C2 7.66421 2.33579 8 2.75 8C3.16421 8 3.5 7.66421 3.5 7.25V4.56066Z"
                fill="white"
            />
        </svg>
    </button>
</div>
